import React from "react";
import { get, isEqual, throttle, times } from "lodash";
import moment from "moment";
import submit from "submit-form";
import MonthGrid from "../../dashboard/calendar/components/MonthGrid";
import MonthWeek from "./components/MonthWeek";
import MetricsDrawer from "./components/MetricsDrawer";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import { DATE_FORMATS } from "../../../constants";

import scriptLoader from "../../../utils/scriptLoader";
import ToolTip from "../../../parts/tooltip-portal";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import SvgIcon from "../../../parts/SvgIcon";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { AthleteLinks } from "../../../parts/athlete-links";
import ClipboardContent from "../../../parts/ClipboardContent";
import BackButton from "../../../parts/BackButton";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

class CalendarMonthView extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showMetricsDrawer: false,
      selectedDates: [],
      daysInEditMode: [],
      loaded: false,
      scheduledSessions: context.locals.scheduledSessions,
      dragging: false,
      draggingFrom: null,
      draggingTo: null,
      expanded: true,
      editCalendar: false,
      opened: false,
    };
    this.locals = context.locals;
    this.handleSelectDate = this.handleSelectDate.bind(this);
    this.handleRemoveMultiple = this.handleRemoveMultiple.bind(this);
    this.loadDroppable = this.loadDroppable.bind(this);
    this.updateWorkout = this.updateWorkout.bind(this);
    this.forceSessionsUpdate = this.forceSessionsUpdate.bind(this);
    this.handleDeselectAll = this.handleDeselectAll.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.toggleMetricsDrawer = this.toggleMetricsDrawer.bind(this);
    this.handleEditDate = this.handleEditDate.bind(this);
    this.drop = null;
  }

  loadDroppable() {
    this.drop = new Droppable.default(
      document.querySelectorAll(".Calendar-Month"),
      {
        draggable: ".day-draggable",
        dropzone: ".day-margin",
        handle: ".drag-icon",
        mirror: {
          constrainDimensions: true,
        },
      }
    );

    this.drop.on("droppable:start", () => {
      this.setState({
        dragging: true,
      });
    });

    this.drop.on("droppable:stop", (evt) => {
      const { dropzone, dragEvent } = evt.data;
      const { originalSource } = dragEvent;
      const draggingFrom = originalSource.id;
      const draggingTo = dropzone.id.replace("container-", "");
      if (draggingFrom === draggingTo) {
        evt.cancel();
        return;
      }
      this.setState({
        dragging: false,
        draggingFrom,
        draggingTo,
      });

      this.forceSessionsUpdate();

      const newDiv = document.getElementById("container-".concat(draggingTo));

      // Since we need to force the state updating, we will have 2 workouts in the new day (duplicated)
      // One belongs to the scheduledSessions state, and the other one is the one moved by Dropabble.js
      // With the timeout we give Droppable.js the time to move it and thus to have that
      // element available in the new component. Then we remove it since it is duplicated
      setTimeout(() => {
        // The "falsy" value will be the one moved by Droppable.js, since it will have the date
        // untouched, belonging to the old day. So we get the one with the "draggingFrom" date
        const toRemoveChild = document.getElementById(draggingFrom);
        newDiv.removeChild(toRemoveChild);
      }, 4);

      this.updateWorkout(draggingFrom, draggingTo);
    });
  }

  handleEditDate({ type = "add", day = "" }) {
    const result =
      type === "add"
        ? [...this.state.daysInEditMode, day]
        : this.state.daysInEditMode.filter((workout) => workout !== day);
    this.setState({
      daysInEditMode: result,
    });
  }

  updateWorkout(dateFrom, dateTo) {
    const athlete =
      this.props.athleteProp ||
      get(this.locals, "query.athlete") ||
      get(this.locals, "athlete._id");

    submit(
      `/actions/exercise/move-day?proxyAthlete=${athlete}&reqSource=calendar&dnd=true`,
      {
        method: "POST",
        body: {
          from: moment.utc(dateFrom, DATE_DAY_FORMAT).startOf("day").toJSON(),
          to: moment(dateTo, DATE_DAY_FORMAT).startOf("day").toJSON(),
          type: "move",
        },
      }
    );
  }

  forceSessionsUpdate() {
    const { draggingFrom, draggingTo, scheduledSessions } = this.state;

    const tempSessions = scheduledSessions;
    const oldDate = tempSessions[draggingFrom];

    if (oldDate) {
      delete tempSessions[draggingFrom];
      oldDate.date = moment(draggingTo, DATE_DAY_FORMAT).format();
      tempSessions[draggingTo] = oldDate;

      this.setState({
        scheduledSessions: tempSessions,
      });
    }
  }

  componentDidMount() {
    scriptLoader(
      "https://cdn.jsdelivr.net/npm/@shopify/draggable@1.0.0-beta.12/lib/droppable.js",
      this.context.locals.query.editCalendar ? () => {} : this.loadDroppable,
      "droppableScript"
    );
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const { locals } = nextContext;
    const { scheduledSessions } = locals;
    if (!isEqual(scheduledSessions, this.state.scheduledSessions)) {
      this.setState({ scheduledSessions });
    }
  }

  handleSelectDate(e, dayString) {
    const selectedDates = this.state.selectedDates;
    if (selectedDates.indexOf(dayString) > -1) {
      selectedDates.splice(selectedDates.indexOf(dayString), 1);
    } else {
      selectedDates.push(dayString);
    }

    this.setState({ selectedDates });
  }

  handleDeselectAll() {
    this.setState({
      selectedDates: [],
    });
  }

  handleSelectAll() {
    const { scheduledSessions } = this.context.locals;
    const newSelectedDates = [];
    Object.keys(scheduledSessions).map((key) => {
      newSelectedDates.push(key);
    });

    this.setState({
      selectedDates: newSelectedDates,
    });
  }

  handleRemoveMultiple(e, singleDate = false) {
    e.preventDefault();
    if (confirm("Are you sure you want to remove the selected workout(s)?")) {
      const days = this.state.selectedDates.length
        ? this.state.selectedDates.map((date) =>
            moment(date).format(DATE_DAY_FORMAT)
          )
        : [moment(singleDate).format(DATE_DAY_FORMAT)];

      const athlete =
        this.props.athleteProp ||
        get(this.locals, "query.athlete") ||
        get(this.locals, "athlete._id");

      submit(
        `/actions/exercise/unschedule-workout?days=${days}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body: { days },
        }
      );

      this.setState({
        selectedDates: [],
      });
    }
  }

  switchMonth(e, direction, activeMonth) {
    e.preventDefault();
    this.setState({
      loaded: false,
      selectedDates: [],
      // This restores the opacity when changing the month with edit modes opened
      daysInEditMode: [],
    });
    submit(`/actions/refresh?`, {
      method: "GET",
      body: {
        month: activeMonth
          .clone()
          .add(direction, "months")
          .format(DATE_YEAR_FORMAT),
      },
    });
  }

  toggleMetricsDrawer(data = null) {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        metrics: this.context.locals.query.metrics ? false : "outcomes",
      },
    });
    this.setState({
      showMetricsDrawer: data !== null ? data : !this.state.showMetricsDrawer,
    });
  }

  toggleDroppable(editCalendar) {
    if (editCalendar && this.drop) {
      this.drop.destroy();
      this.drop = null;
    } else if (!editCalendar && !this.drop) {
      this.loadDroppable();
    }
  }

  render() {
    const {
      activeMonth,
      query,
      startOfFirstWeek,
      activeUser,
      activeUserToRequest,
      athlete,
      pathname,
    } = this.context.locals;

    const { scheduledSessions, selectedDates, opened } = this.state;
    const totalScheduledSessions = Object.keys(scheduledSessions).length;
    const intensityMeasure =
      get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
        ? "RPE"
        : "RiR";
    const isRiR = intensityMeasure === "RiR";
    const allSelected = totalScheduledSessions === selectedDates.length;

    return (
      <main className={`Calendar${query.editCalendar ? " edit-modex" : ""}`}>
        <header>
          <section>
            <hgroup>
              {this.props.showBack && <BackButton />}
              {selectedDates.length > 0 ? (
                <>
                  {totalScheduledSessions > 0 && (
                    <button
                      data-button-type="secondary"
                      onClick={
                        allSelected
                          ? this.handleDeselectAll
                          : this.handleSelectAll
                      }
                    >
                      <SvgIcon
                        className="mr-2"
                        src={
                          allSelected
                            ? "/img/new-icons/checked-square.svg"
                            : "/img/new-icons/unchecked-square.svg"
                        }
                        noFilterOnLight={allSelected}
                      />
                      {allSelected ? "Deselect All" : "Select All"}
                    </button>
                  )}
                  <p className="p4">({selectedDates.length} selected)</p>
                </>
              ) : (
                <>
                  <button
                    className="bg-[--dark-grey] !pr-2"
                    onClick={() => {
                      this.toggleDroppable(!query.editCalendar);
                      submit(`/actions/refresh/?`, {
                        method: "GET",
                        body: {
                          editCalendar: !query.editCalendar,
                        },
                      });
                    }}
                  >
                    Edit Mode
                    <input
                      type="checkbox"
                      name="editCalendar"
                      id="editCalendar"
                      role="switch"
                      checked={query.editCalendar}
                    />
                    <ToolTip body="Edit workouts and build training programs directly inside your athletes calendar">
                      ?
                    </ToolTip>
                  </button>
                  <a
                    href={`/actions/refresh?month=${moment().format(
                      DATE_YEAR_FORMAT
                    )}`}
                    data-button-type="outline"
                    role="button"
                  >
                    This Month
                  </a>
                </>
              )}
            </hgroup>

            <hgroup>
              <div role="month-handler">
                <div className="!bg-[--dark-grey]">
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, -1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-left.svg" />
                  </button>
                  <span>{activeMonth.format("MMMM, YYYY")}</span>
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, 1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-right.svg" />
                  </button>
                </div>
              </div>
            </hgroup>

            <hgroup>
              {selectedDates.length > 0 ? (
                <>
                  <button
                    data-button-type="secondary"
                    onClick={() => this.setState({ selectedDates: [] })}
                  >
                    Cancel
                  </button>
                  <button
                    data-button-type="secondary"
                    className="!text-[--danger]"
                    onClick={this.handleRemoveMultiple}
                    disabled={selectedDates.length === 0}
                  >
                    <SvgIcon
                      className="mr-2"
                      src={"/img/new-icons/red-trash.svg"}
                      noFilterOnLight={true}
                    />
                    Remove
                  </button>
                </>
              ) : (
                <>
                  <ToggleLinksInput
                    name="view"
                    options={[
                      {
                        text: "Month",
                        value: "month",
                        href: `/actions/refresh?view=month&month=${
                          query.week
                            ? moment(query.week, DATE_DAY_FORMAT).format(
                                DATE_YEAR_FORMAT
                              )
                            : query.date
                            ? moment(query.date, DATE_DAY_FORMAT).format(
                                DATE_YEAR_FORMAT
                              )
                            : ""
                        }&week=&date=`,
                      },
                      {
                        text: "Week",
                        value: "week",
                        href: `/actions/refresh?view=week&week=${
                          moment().isSame(query.month, "month")
                            ? moment().format(DATE_DAY_FORMAT)
                            : moment(query.month, DATE_YEAR_FORMAT).format(
                                DATE_DAY_FORMAT
                              )
                        }&month=&date=`,
                      },
                      {
                        text: "Day",
                        value: "day",
                        href: `/actions/refresh?view=day&date=${
                          moment().isSame(query.month, "month")
                            ? moment().format(DATE_DAY_FORMAT)
                            : moment(query.month, DATE_YEAR_FORMAT).format(
                                DATE_DAY_FORMAT
                              )
                        }&month=&week=`,
                      },
                    ]}
                  />
                  <details role="list" open>
                    <summary
                      onClick={(e) => {
                        e.preventDefault();
                        this.setState({ opened: !opened });
                      }}
                      onBlur={(e) => {
                        e.preventDefault();
                        this.setState({ opened: false });
                      }}
                      aria-haspopup="listbox"
                    >
                      More
                      <SvgIcon
                        src={
                          opened
                            ? "/img/svg/angle-up.svg"
                            : "/img/svg/angle-down.svg"
                        }
                      />
                    </summary>
                    <ul role="listbox" className={opened ? "opened" : ""}>
                      <div className="list-title">
                        <span>Settings</span>
                        <div />
                      </div>
                      <li
                        onMouseDown={() => {
                          submit("/coaching-portal/settings/preferences");
                        }}
                      >
                        <span>Edit Calendar Settings</span>
                        <SvgIcon src="/img/new-icons/export.svg" />
                      </li>
                      <li
                        onMouseDown={() => {
                          this.toggleMetricsDrawer();
                        }}
                      >
                        <span>Training Metrics</span>
                        <SvgIcon src="/img/new-icons/export.svg" />
                      </li>

                      {!query.editCalendar ? (
                        <>
                          <li
                            onMouseDown={() =>
                              this.setState({ expanded: !this.state.expanded })
                            }
                          >
                            <span>Expand Calendar</span>
                            <SvgIcon
                              src={
                                this.state.expanded
                                  ? "/img/new-icons/checked-square.svg"
                                  : "/img/new-icons/unchecked-square.svg"
                              }
                              noFilterOnLight={this.state.expanded}
                              classNames="-mr-[0.15rem]"
                            />
                          </li>

                          <div className="list-title mt-3">
                            <span>Legend</span>
                            <div />
                          </div>
                          <div className="legend-item">
                            <div className="circle-status completed" />
                            Fully completed
                          </div>
                          <div className="legend-item">
                            <div className="circle-status partial" />
                            Partially complete
                          </div>
                          <div className="legend-item">
                            <div className="circle-status target" />
                            Target
                          </div>
                        </>
                      ) : null}
                    </ul>
                  </details>
                </>
              )}
            </hgroup>
          </section>
        </header>
        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athlete}
            pathName={pathname}
            classNames={"only-show-mobile"}
            query={query}
          />
          <div>
            <div className="athlete-select dropdown-athlete">
              <div
                className="avatar"
                style={{
                  backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
                }}
              />
              <AthleteSelect locals={this.context.locals} />
            </div>
            <ToggleLinksInput
              name="view"
              options={[
                {
                  text: "Month",
                  value: "month",
                  href: `/actions/refresh?view=month&month=${
                    query.week
                      ? moment(query.week, DATE_DAY_FORMAT).format(
                          DATE_YEAR_FORMAT
                        )
                      : query.date
                      ? moment(query.date, DATE_DAY_FORMAT).format(
                          DATE_YEAR_FORMAT
                        )
                      : ""
                  }&week=&date=`,
                },

                {
                  text: "Day",
                  value: "day",
                  href: `/actions/refresh?view=day&date=${
                    moment().isSame(query.month, "month")
                      ? moment().format(DATE_DAY_FORMAT)
                      : moment(query.month, DATE_YEAR_FORMAT).format(
                          DATE_DAY_FORMAT
                        )
                  }&month=&week=`,
                },
              ]}
            />
          </div>
        </div>

        <MonthGrid
          editCalendar={query.editCalendar}
          startDay={activeUser.startingCalendarDay}
        >
          {times(6, (week) => {
            const startOfWeek = startOfFirstWeek.clone().add(week, "weeks");
            if (
              !(
                week === 5 &&
                startOfWeek.clone().month() !== activeMonth.clone().month()
              )
            ) {
              return (
                <MonthWeek
                  activeMonth={activeMonth.clone()}
                  key={week}
                  startOfWeek={startOfWeek}
                  handleSelectDate={this.handleSelectDate}
                  selectedDates={this.state.selectedDates}
                  scheduledSessions={this.state.scheduledSessions}
                  handleRemoveMultiple={this.handleRemoveMultiple}
                  dragging={this.state.dragging}
                  expanded={this.state.expanded}
                  editCalendar={query.editCalendar}
                  handleEditDate={this.handleEditDate}
                  daysInEditMode={this.state.daysInEditMode}
                  removing={selectedDates.length > 0}
                />
              );
            }
          })}

          {this.state.scheduledSessions &&
            Object.keys(this.state.scheduledSessions).length <= 0 && (
              <div className="no-workouts-message only-show-mobile">
                <span>No Workout Found</span>
                <span>
                  No workouts scheduled for {moment(activeMonth).format("MMMM")}
                </span>
              </div>
            )}
        </MonthGrid>
        {query.metrics ? (
          <MetricsDrawer
            exerciseSelected={query.exercise}
            toggleMetricsDrawer={this.toggleMetricsDrawer}
            calendarMetrics={this.context.locals.calendarMetrics}
            type={query.metrics}
            userUnit={activeUser.units}
            intensityMeasure={intensityMeasure}
            isRiR={isRiR}
          />
        ) : null}
        <ClipboardContent
          query={query.exercisesInClipboard}
          locals={this.context.locals}
        />
      </main>
    );
  }
}

export default CalendarMonthView;
